import {
  CoordinatePatient,
  CoordinateProvider,
  ProviderMatch,
  ProviderMatchScores,
  CLINICAL_NEED_LABELS,
} from '~/types/coordinate';

function scoreTimeToStart(avgStartDays: number): number {
  return Math.max(0, 100 - avgStartDays * 10);
}

function scoreClinicalFit(provider: CoordinateProvider, patient: CoordinatePatient): number {
  if (patient.clinicalNeeds.length === 0) return 100;
  const matched = patient.clinicalNeeds.filter(need => provider.services.includes(need)).length;
  return (matched / patient.clinicalNeeds.length) * 100;
}

function scoreDistance(distanceMiles: number): number {
  return Math.max(0, 100 - distanceMiles * 2);
}

function scoreQuality(qualityRating: number): number {
  return (qualityRating / 5) * 100;
}

function passesHardFilters(provider: CoordinateProvider, patient: CoordinatePatient): boolean {
  const dispositionMatch = provider.supportedDispositions.includes(patient.disposition);
  const insuranceMatch = provider.acceptedInsurance.includes(patient.insurance);
  const geoMatch =
    provider.coverageZips.includes(patient.zip) || provider.distanceMiles <= 30;
  const serviceMatch = patient.clinicalNeeds.every(need => provider.services.includes(need));
  return dispositionMatch && insuranceMatch && geoMatch && serviceMatch;
}

function buildWhyRecommended(
  provider: CoordinateProvider,
  patient: CoordinatePatient,
  scores: ProviderMatchScores
): string[] {
  const bullets: { priority: number; text: string }[] = [];

  if (scores.acceptanceLikelihood >= 70) {
    bullets.push({
      priority: scores.acceptanceLikelihood,
      text: `High acceptance likelihood (${Math.round(provider.acceptanceLikelihood * 100)}%) based on similar case history`,
    });
  }

  if (provider.avgStartDays <= 2) {
    bullets.push({
      priority: scores.timeToStart,
      text: `Rapid start capability — average ${provider.avgStartDays} day${provider.avgStartDays === 1 ? '' : 's'} to begin care`,
    });
  } else if (provider.avgStartDays <= 5) {
    bullets.push({
      priority: scores.timeToStart,
      text: `Expected start within ${provider.avgStartDays} days meets discharge timeline`,
    });
  }

  const matchedNeeds = patient.clinicalNeeds.filter(need => provider.services.includes(need));
  if (matchedNeeds.length > 0) {
    const needLabels = matchedNeeds.map(n => CLINICAL_NEED_LABELS[n]).join(', ');
    bullets.push({
      priority: scores.clinicalFit,
      text: `Covers all required services: ${needLabels}`,
    });
  }

  if (provider.healthSystemPreferred) {
    bullets.push({
      priority: 80,
      text: 'Preferred health system partner with established care protocols',
    });
  }

  if (provider.qualityRating >= 4.5) {
    bullets.push({
      priority: scores.quality,
      text: `Top-rated provider (${provider.qualityRating}/5.0) in this service area`,
    });
  }

  if (provider.currentCapacitySignal === 'high') {
    bullets.push({
      priority: 65,
      text: 'Currently showing strong capacity — higher acceptance likelihood',
    });
  } else if (provider.currentCapacitySignal === 'low') {
    bullets.push({
      priority: 20,
      text: 'Capacity signal is low — acceptance likelihood reduced',
    });
  }

  bullets.sort((a, b) => b.priority - a.priority);
  return bullets.slice(0, 4).map(b => b.text);
}

function fitLabel(totalScore: number): 'Best Fit' | 'Strong Option' | 'Backup' {
  if (totalScore >= 75) return 'Best Fit';
  if (totalScore >= 50) return 'Strong Option';
  return 'Backup';
}

export function scoreProviders(
  patient: CoordinatePatient,
  providers: CoordinateProvider[]
): ProviderMatch[] {
  const results: ProviderMatch[] = [];

  for (const provider of providers) {
    const passed = passesHardFilters(provider, patient);
    if (!passed) continue;

    const capacityMultiplier =
      provider.currentCapacitySignal === 'high' ? 1.15 :
      provider.currentCapacitySignal === 'medium' ? 1.0 : 0.70;

    const scores: ProviderMatchScores = {
      acceptanceLikelihood: Math.min(100, provider.acceptanceLikelihood * 100 * capacityMultiplier),
      timeToStart: scoreTimeToStart(provider.avgStartDays),
      clinicalFit: scoreClinicalFit(provider, patient),
      distance: scoreDistance(provider.distanceMiles),
      quality: scoreQuality(provider.qualityRating),
      healthSystemPreference: provider.healthSystemPreferred ? 100 : 0,
    };

    const totalScore =
      0.35 * scores.acceptanceLikelihood +
      0.20 * scores.timeToStart +
      0.15 * scores.clinicalFit +
      0.10 * scores.distance +
      0.10 * scores.quality +
      0.10 * scores.healthSystemPreference;

    results.push({
      provider,
      totalScore: Math.round(totalScore),
      fitLabel: fitLabel(totalScore),
      scores,
      passedHardFilters: true,
      whyRecommended: buildWhyRecommended(provider, patient, scores),
    });
  }

  return results
    .sort((a, b) => b.totalScore - a.totalScore)
    .slice(0, 5);
}
